import { type EventVisibilitySubject } from "./eventVisibility";
import {
  type DocumentPanelData,
  type File,
  type ImagePanelConfig,
  type TopicData,
  type PanelState,
  type State,
} from "./schema";
import {
  type CoordinateUnit,
  Datasource,
  Layout,
  LayoutItem,
  LogPanelConfig,
  PlotPanelConfig,
  PlotSeriesConfig,
  ThreeDEntity,
  ThreeDPanelConfig,
  // Aliased away from the DOM's `File`, which this module also sees.
  type File as ThreeDFile,
  type ThreeDJoint,
  type ThreeDJointValue,
} from "./schema/v1";
import { type ResolvedWorkspaceSettings } from "./settings";

// Base
export enum ActionType {
  AddClipToImagePanel = "AddClipToImagePanel",
  AddDataToLogPanel = "addDataToLogPanel",
  AddDataToRawMessagePanel = "AddDataToRawMessagePanel",
  AddPathToMapPanel = "AddPathToMapPanel",
  AddSeriesToPlotPanel = "AddSeriesToPlotPanel",
  CreateDocumentPanelWithFile = "CreateDocumentPanelWithFile",
  CreatePanel = "CreatePanel",
  CreateThreeDPanelWithEntity = "CreateThreeDPanelWithEntity",
  MovePanel = "MovePanel",
  PutImagePanelData = "PutImagePanelData",
  PutFiles = "PutFiles",
  RemoveAllPanels = "RemoveAllPanels",
  RemoveClipFromImagePanel = "RemoveClipFromImagePanel",
  RemoveMessagePathFromLogPanel = "RemoveMessagePathFromLogPanel",
  RemoveMessagePathFromRawMessagePanel = "RemoveMessagePathFromRawMessagePanel",
  RemovePanel = "RemovePanel",
  RemovePathFromMapPanel = "RemovePathFromMapPanel",
  RemoveSeriesFromPlotPanel = "RemoveSeriesFromPlotPanel",
  ReplaceState = "ReplaceState",
  ReplacePanelsAndLayout = "ReplacePanelsAndLayout",
  ResizeLayouts = "ResizeLayouts",
  SetAllLayoutsResizing = "SetAllLayoutsResizing",
  SetDataSource = "SetDataSource",
  SetGroupMemberIndices = "SetGroupMemberIndices",
  FillGroupMembersFromRead = "FillGroupMembersFromRead",
  SetDatasources = "SetDatasources",
  ClearEventOverrides = "ClearEventOverrides",
  SetEventVisibility = "SetEventVisibility",
  SetEventsVisibility = "SetEventsVisibility",
  SetImagePanelConfig = "SetImagePanelConfig",
  SetLayoutResizing = "SetLayoutResizing",
  SetLogPanelConfig = "SetLogPanelConfig",
  SetPanelTitle = "SetPanelTitle",
  SetPathCoordinateUnits = "SetPathCoordinateUnits",
  SetPathStyle = "SetPathStyle",
  SetPathVisibility = "SetPathVisibility",
  SetPlotPanelConfig = "SetPlotPanelConfig",
  SetSeriesConfig = "SetSeriesConfig",
  SetSeriesVisibility = "SetSeriesVisibility",
  SetWorkspaceSettings = "SetWorkspaceSettings",
  SetDocumentPanelFile = "SetDocumentPanelFile",
  SetThreeDPanelConfig = "SetThreeDPanelConfig",
  AddThreeDEntity = "AddThreeDEntity",
  SetThreeDModel = "SetThreeDModel",
  SetThreeDModelJoints = "SetThreeDModelJoints",
  UpdateThreeDEntity = "UpdateThreeDEntity",
  RenameThreeDEntity = "RenameThreeDEntity",
  RemoveThreeDEntity = "RemoveThreeDEntity",
}

export enum LayoutOrientation {
  TOP,
  RIGHT,
  BOTTOM,
  LEFT,
}

export interface Action<T> {
  payload: T;
  type: ActionType;
}

// AddClipToImagePanel
interface AddClipToImagePanelPayload {
  panelId: string;
  data: TopicData;
}
interface AddClipToImagePanelAction extends Action<AddClipToImagePanelPayload> {}
export function isAddClipToImagePanelAction(
  action: Action<unknown>,
): action is AddClipToImagePanelAction {
  return action.type === ActionType.AddClipToImagePanel;
}
export function addClipToImagePanel(
  panelId: string,
  data: TopicData,
): AddClipToImagePanelAction {
  return {
    payload: {
      panelId,
      data,
    },
    type: ActionType.AddClipToImagePanel,
  };
}

// AddDataToLogPanel
interface AddDataToLogPanelPayload {
  data: TopicData;
  panelId: string;
}
interface AddDataToLogPanelAction extends Action<AddDataToLogPanelPayload> {}
export function isAddDataToLogPanelAction(
  action: Action<unknown>,
): action is AddDataToLogPanelAction {
  return action.type === ActionType.AddDataToLogPanel;
}
export function addDataToLogPanel(
  panelId: string,
  data: TopicData,
): AddDataToLogPanelAction {
  return {
    payload: {
      data,
      panelId,
    },
    type: ActionType.AddDataToLogPanel,
  };
}

// AddDataToRawMessagePanel
interface AddDataToRawMessagePanelPayload {
  data: TopicData;
  panelId: string;
}
interface AddDataToRawMessagePanelAction extends Action<AddDataToRawMessagePanelPayload> {}
export function isAddDataToRawMessagePanelAction(
  action: Action<unknown>,
): action is AddDataToRawMessagePanelAction {
  return action.type === ActionType.AddDataToRawMessagePanel;
}
export function addDataToRawMessagePanel(
  panelId: string,
  data: TopicData,
): AddDataToRawMessagePanelAction {
  return {
    payload: {
      panelId,
      data,
    },
    type: ActionType.AddDataToRawMessagePanel,
  };
}

// AddPathToMapPanel
interface AddPathToMapPanelPayload {
  coordinateUnits?: {
    lat?: CoordinateUnit;
    lon?: CoordinateUnit;
  };
  data: TopicData[];
  panelId: string;
}
interface AddPathToMapPanelAction extends Action<AddPathToMapPanelPayload> {}
export function isAddPathToMapPanelAction(
  action: Action<unknown>,
): action is AddPathToMapPanelAction {
  return action.type === ActionType.AddPathToMapPanel;
}
export function addPathToMapPanel(
  panelId: string,
  data: TopicData[],
  coordinateUnits?: { lat?: CoordinateUnit; lon?: CoordinateUnit },
): AddPathToMapPanelAction {
  return {
    payload: {
      coordinateUnits,
      data,
      panelId,
    },
    type: ActionType.AddPathToMapPanel,
  };
}

// AddSeriesToPlotPanel
interface AddSeriesToPlotPanelPayload {
  data: TopicData;
  panelId: string;
  /**
   * Series id to use instead of generating one. Callers that need to
   * reference the series after creation (e.g. panel settings spotlighting the
   * row it just added) supply a freshly generated UUID. The reducer ignores
   * the action when the panel already has a series with this id.
   */
  id?: string;
}
interface AddSeriesToPlotPanelAction extends Action<AddSeriesToPlotPanelPayload> {}
export function isAddSeriesToPlotPanelAction(
  action: Action<unknown>,
): action is AddSeriesToPlotPanelAction {
  return action.type === ActionType.AddSeriesToPlotPanel;
}
export function addSeriesToPlotPanel(
  panelId: string,
  data: TopicData,
  id?: string,
): AddSeriesToPlotPanelAction {
  return {
    payload: {
      data,
      id,
      panelId,
    },
    type: ActionType.AddSeriesToPlotPanel,
  };
}

// CreatePanel
export interface Placement {
  siblingLayout: Layout | LayoutItem;
  orientation: LayoutOrientation;
}
export interface CreatePanelPayload {
  data: TopicData[] | null;
  type: PanelState["type"];
  placement?: Placement;
  /**
   * Panel id to use instead of generating one. Callers that need to reference
   * the panel after creation (e.g. workspace actions that report the created
   * panel back to an AI tool) supply a freshly generated UUID. The reducer
   * ignores the action when a panel with this id already exists.
   */
  id?: string;
}
interface CreatePanelAction extends Action<CreatePanelPayload> {}
export function isCreatePanelAction(
  action: Action<unknown>,
): action is CreatePanelAction {
  return action.type === ActionType.CreatePanel;
}
export function createPanelAction(
  data: TopicData[] | null,
  type: PanelState["type"],
  placement?: Placement,
  id?: string,
): CreatePanelAction {
  return {
    payload: {
      data,
      type,
      placement,
      id,
    },
    type: ActionType.CreatePanel,
  };
}

// CreateThreeDPanelWithEntity
export interface CreateThreeDPanelWithEntityPayload {
  /** The panel's one entity, declared, drawn and targeted by its camera. */
  entity: ThreeDEntity;
  placement?: Placement;
  /** As for {@link CreatePanelPayload.id}. */
  id?: string;
}
interface CreateThreeDPanelWithEntityAction extends Action<CreateThreeDPanelWithEntityPayload> {}
export function isCreateThreeDPanelWithEntityAction(
  action: Action<unknown>,
): action is CreateThreeDPanelWithEntityAction {
  return action.type === ActionType.CreateThreeDPanelWithEntity;
}
/**
 * Creates a 3D panel around one entity, where `CreatePanel` creates one
 * around its default sphere: what a point cloud topic sent from the topic tree
 * opens into.
 */
export function createThreeDPanelWithEntity(
  entity: ThreeDEntity,
  placement?: Placement,
  id?: string,
): CreateThreeDPanelWithEntityAction {
  return {
    payload: {
      entity,
      placement,
      id,
    },
    type: ActionType.CreateThreeDPanelWithEntity,
  };
}

// CreateDocumentPanelWithFile
interface CreateDocumentPanelWithFilePayload {
  data: DocumentPanelData;
  placement?: Placement;
}
interface CreateDocumentPanelWithFileAction extends Action<CreateDocumentPanelWithFilePayload> {}
export function isCreateDocumentPanelWithFileAction(
  action: Action<unknown>,
): action is CreateDocumentPanelWithFileAction {
  return action.type === ActionType.CreateDocumentPanelWithFile;
}
export function createDocumentPanelWithFile(
  data: DocumentPanelData,
  placement?: Placement,
): CreateDocumentPanelWithFileAction {
  return {
    payload: {
      data,
      placement,
    },
    type: ActionType.CreateDocumentPanelWithFile,
  };
}

// MovePanel
export interface MovePanelPayload {
  panelLayout: LayoutItem;
  placement: Placement;
}
interface MovePanelAction extends Action<MovePanelPayload> {}
export function isMovePanelAction(
  action: Action<unknown>,
): action is MovePanelAction {
  return action.type === ActionType.MovePanel;
}
export function movePanelAction(
  panelLayout: LayoutItem,
  placement: Placement,
): MovePanelAction {
  return {
    payload: {
      panelLayout,
      placement,
    },
    type: ActionType.MovePanel,
  };
}

// PutImagePanelData
interface PutImagePanelDataPayload {
  panelId: string;
  data: TopicData;
}
interface PutImagePanelDataAction extends Action<PutImagePanelDataPayload> {}
export function isPutImagePanelDataAction(
  action: Action<unknown>,
): action is PutImagePanelDataAction {
  return action.type === ActionType.PutImagePanelData;
}
export function putImagePanelData(
  panelId: string,
  data: TopicData,
): PutImagePanelDataAction {
  return {
    payload: {
      panelId,
      data,
    },
    type: ActionType.PutImagePanelData,
  };
}

// PutFiles
/** @deprecated Use setDatasources instead */
interface PutFilesPayload {
  files: File[];
}
interface PutFilesAction extends Action<PutFilesPayload> {}
export function isPutFilesAction(
  action: Action<unknown>,
): action is PutFilesAction {
  return action.type === ActionType.PutFiles;
}
/** @deprecated Use setDatasources instead */
export function putFiles(files: File[]): PutFilesAction {
  return {
    payload: { files },
    type: ActionType.PutFiles,
  };
}

// SetDatasources
interface SetDatasourcesPayload {
  datasources: Datasource[];
}
interface SetDatasourcesAction extends Action<SetDatasourcesPayload> {}
export function isSetDatasourcesAction(
  action: Action<unknown>,
): action is SetDatasourcesAction {
  return action.type === ActionType.SetDatasources;
}
export function setDatasources(
  datasources: Datasource[],
): SetDatasourcesAction {
  return {
    payload: { datasources },
    type: ActionType.SetDatasources,
  };
}

// RemoveAllPanels
interface RemoveAllPanelsAction extends Action<undefined> {}
export function isRemoveAllPanelsAction(
  action: Action<unknown>,
): action is RemoveAllPanelsAction {
  return action.type === ActionType.RemoveAllPanels;
}
export function removeAllPanels(): RemoveAllPanelsAction {
  return {
    payload: undefined,
    type: ActionType.RemoveAllPanels,
  };
}

// RemoveClipFromImagePAnel
interface RemoveClipFromImagePanelPayload {
  panelId: string;
  clipId: string;
}
interface RemoveClipFromImagePanelAction extends Action<RemoveClipFromImagePanelPayload> {}
export function isRemoveClipFromImagePanelAction(
  action: Action<unknown>,
): action is RemoveClipFromImagePanelAction {
  return action.type === ActionType.RemoveClipFromImagePanel;
}
export function removeClipFromImagePanel(
  panelId: string,
  clipId: string,
): RemoveClipFromImagePanelAction {
  return {
    payload: {
      panelId,
      clipId,
    },
    type: ActionType.RemoveClipFromImagePanel,
  };
}

// RemoveDataFromLogPanel
interface RemoveMessagePathFromLogPanelPayload {
  panelId: string;
  messagePathId: string;
}
interface RemoveMessagePathFromLogPanelAction extends Action<RemoveMessagePathFromLogPanelPayload> {}
export function isRemoveMessagePathFromLogPanelAction(
  action: Action<unknown>,
): action is RemoveMessagePathFromLogPanelAction {
  return action.type === ActionType.RemoveMessagePathFromLogPanel;
}
export function removeMessagePathFromLogPanel(
  panelId: string,
  messagePathId: string,
): RemoveMessagePathFromLogPanelAction {
  return {
    payload: {
      panelId,
      messagePathId,
    },
    type: ActionType.RemoveMessagePathFromLogPanel,
  };
}

// RemoveMessagePathFromRawMessagePanel
interface RemoveMessagePathFromRawMessagePanelPayload {
  panelId: string;
  messagePathId: string;
}
interface RemoveMessagePathFromRawMessagePanelAction extends Action<RemoveMessagePathFromRawMessagePanelPayload> {}
export function isRemoveMessagePathFromRawMessagePanelAction(
  action: Action<unknown>,
): action is RemoveMessagePathFromRawMessagePanelAction {
  return action.type === ActionType.RemoveMessagePathFromRawMessagePanel;
}
export function removeMessagePathFromRawMessagePanel(
  panelId: string,
  messagePathId: string,
): RemoveMessagePathFromRawMessagePanelAction {
  return {
    payload: {
      panelId,
      messagePathId,
    },
    type: ActionType.RemoveMessagePathFromRawMessagePanel,
  };
}

// RemovePanel
export interface RemovePanelPayload {
  panelId: string;
}
interface RemovePanelAction extends Action<RemovePanelPayload> {}
export function isRemovePanelAction(
  action: Action<unknown>,
): action is RemovePanelAction {
  return action.type === ActionType.RemovePanel;
}
export function removePanel(panelId: string): RemovePanelAction {
  return {
    payload: {
      panelId,
    },
    type: ActionType.RemovePanel,
  };
}

// RemovePathFromMapPanel
interface RemovePathFromMapPanelPayload {
  panelId: string;
  pathId: string;
}
interface RemovePathFromMapPanelAction extends Action<RemovePathFromMapPanelPayload> {}
export function isRemovePathFromMapPanelAction(
  action: Action<unknown>,
): action is RemovePathFromMapPanelAction {
  return action.type === ActionType.RemovePathFromMapPanel;
}
export function removePathFromMapPanel(
  panelId: string,
  pathId: string,
): RemovePathFromMapPanelAction {
  return {
    payload: {
      panelId,
      pathId,
    },
    type: ActionType.RemovePathFromMapPanel,
  };
}

// RemoveSeriesFromPlotPanel
interface RemoveSeriesFromPlotPanelPayload {
  panelId: string;
  seriesId: string;
}
interface RemoveSeriesFromPlotPanelAction extends Action<RemoveSeriesFromPlotPanelPayload> {}
export function isRemoveSeriesFromPlotPanelAction(
  action: Action<unknown>,
): action is RemoveSeriesFromPlotPanelAction {
  return action.type === ActionType.RemoveSeriesFromPlotPanel;
}
export function removeSeriesFromPlotPanel(
  panelId: string,
  seriesId: string,
): RemoveSeriesFromPlotPanelAction {
  return {
    payload: {
      panelId,
      seriesId,
    },
    type: ActionType.RemoveSeriesFromPlotPanel,
  };
}

// ReplaceState
// Intended only for use by the StateEditor component -- use more specific actions in other contexts.
interface ReplaceStatePayload {
  state: State;
}
interface ReplaceStateAction extends Action<ReplaceStatePayload> {}
export function isReplaceStateAction(
  action: Action<unknown>,
): action is ReplaceStateAction {
  return action.type === ActionType.ReplaceState;
}
export function replaceState(state: State): ReplaceStateAction {
  return {
    payload: {
      state,
    },
    type: ActionType.ReplaceState,
  };
}

// ReplacePanelsAndLayout
interface ReplacePanelsAndLayoutPayload {
  panels: State["panels"];
  layout: State["layout"];
}
interface ReplacePanelsAndLayoutAction extends Action<ReplacePanelsAndLayoutPayload> {}
export function isReplacePanelsAndLayoutAction(
  action: Action<unknown>,
): action is ReplacePanelsAndLayoutAction {
  return action.type === ActionType.ReplacePanelsAndLayout;
}
export function replacePanelsAndLayout(
  panels: State["panels"],
  layout: State["layout"],
): ReplacePanelsAndLayoutAction {
  return {
    payload: {
      panels,
      layout,
    },
    type: ActionType.ReplacePanelsAndLayout,
  };
}

// SetPathStyle
interface SetPathStylePayload {
  panelId: string;
  pathId: string;
  style: Record<string, unknown>;
}
interface SetPathStyleAction extends Action<SetPathStylePayload> {}
export function isSetPathStyleAction(
  action: Action<unknown>,
): action is SetPathStyleAction {
  return action.type === ActionType.SetPathStyle;
}
export function setPathStyle(
  panelId: string,
  pathId: string,
  style: Record<string, unknown>,
): SetPathStyleAction {
  return {
    payload: {
      panelId,
      pathId,
      style,
    },
    type: ActionType.SetPathStyle,
  };
}

// SetPathVisibility
interface SetPathVisibilityPayload {
  panelId: string;
  pathId: string;
  visible: boolean;
}
interface SetPathVisibilityAction extends Action<SetPathVisibilityPayload> {}
export function isSetPathVisibilityAction(
  action: Action<unknown>,
): action is SetPathVisibilityAction {
  return action.type === ActionType.SetPathVisibility;
}
export function setPathVisibility(
  panelId: string,
  pathId: string,
  visible: boolean,
): SetPathVisibilityAction {
  return {
    payload: {
      panelId,
      pathId,
      visible,
    },
    type: ActionType.SetPathVisibility,
  };
}

// SetPathCoordinateUnits
interface SetPathCoordinateUnitsPayload {
  panelId: string;
  pathId: string;
  coordinateUnits: {
    lat?: CoordinateUnit;
    lon?: CoordinateUnit;
  };
}
interface SetPathCoordinateUnitsAction extends Action<SetPathCoordinateUnitsPayload> {}
export function isSetPathCoordinateUnitsAction(
  action: Action<unknown>,
): action is SetPathCoordinateUnitsAction {
  return action.type === ActionType.SetPathCoordinateUnits;
}
export function setPathCoordinateUnits(
  panelId: string,
  pathId: string,
  coordinateUnits: {
    lat?: CoordinateUnit;
    lon?: CoordinateUnit;
  },
): SetPathCoordinateUnitsAction {
  return {
    payload: {
      panelId,
      pathId,
      coordinateUnits,
    },
    type: ActionType.SetPathCoordinateUnits,
  };
}

// SetSeriesConfig
interface SetSeriesConfigPayload {
  panelId: string;
  seriesId: string;
  config: Partial<PlotSeriesConfig>;
}
interface SetSeriesConfigAction extends Action<SetSeriesConfigPayload> {}
export function isSetSeriesConfigAction(
  action: Action<unknown>,
): action is SetSeriesConfigAction {
  return action.type === ActionType.SetSeriesConfig;
}
export function setSeriesConfig(
  panelId: string,
  seriesId: string,
  config: Partial<PlotSeriesConfig>,
): SetSeriesConfigAction {
  return {
    payload: {
      panelId,
      seriesId,
      config,
    },
    type: ActionType.SetSeriesConfig,
  };
}

// FillGroupMembersFromRead
interface FillGroupMembersFromReadPayload {
  panelId: string;
  groupId: string;
  /**
   * The data source the read was of. A report for any other data source than
   * the group's current one was made before the group was re-pointed, and is
   * ignored.
   */
  data: TopicData;
  /** How many elements the first read observed. */
  elementCount: number;
  /** Whether those elements are of a kind the plot draws. */
  plottable: boolean;
}
interface FillGroupMembersFromReadAction extends Action<FillGroupMembersFromReadPayload> {}
export function isFillGroupMembersFromReadAction(
  action: Action<unknown>,
): action is FillGroupMembersFromReadAction {
  return action.type === ActionType.FillGroupMembersFromRead;
}
/**
 * Reports what a read observed of a group's array. A whole-array group with no
 * members is filled with one member per element, up to `MAX_GROUP_MEMBERS`,
 * when the elements are numbers; any other group is left as it is, since a
 * read never removes a member and a range names its members itself.
 */
export function fillGroupMembersFromRead(
  panelId: string,
  groupId: string,
  data: TopicData,
  elementCount: number,
  plottable: boolean,
): FillGroupMembersFromReadAction {
  return {
    payload: {
      panelId,
      groupId,
      data,
      elementCount,
      plottable,
    },
    type: ActionType.FillGroupMembersFromRead,
  };
}

// SetGroupMemberIndices
interface SetGroupMemberIndicesPayload {
  panelId: string;
  groupId: string;
  indices: number[];
}
interface SetGroupMemberIndicesAction extends Action<SetGroupMemberIndicesPayload> {}
export function isSetGroupMemberIndicesAction(
  action: Action<unknown>,
): action is SetGroupMemberIndicesAction {
  return action.type === ActionType.SetGroupMemberIndices;
}
/**
 * Makes exactly `indices` the group's members, in that order and at most
 * `MAX_GROUP_MEMBERS` of them: a member whose index survives keeps its
 * id and config, the rest are fresh.
 */
export function setGroupMemberIndices(
  panelId: string,
  groupId: string,
  indices: number[],
): SetGroupMemberIndicesAction {
  return {
    payload: {
      panelId,
      groupId,
      indices,
    },
    type: ActionType.SetGroupMemberIndices,
  };
}

// SetSeriesVisibility
interface SetSeriesVisibilityPayload {
  panelId: string;
  seriesId: string;
  visible: boolean;
}
interface SetSeriesVisibilityAction extends Action<SetSeriesVisibilityPayload> {}
export function isSetSeriesVisibilityAction(
  action: Action<unknown>,
): action is SetSeriesVisibilityAction {
  return action.type === ActionType.SetSeriesVisibility;
}
export function setSeriesVisibility(
  panelId: string,
  seriesId: string,
  visible: boolean,
): SetSeriesVisibilityAction {
  return {
    payload: {
      panelId,
      seriesId,
      visible,
    },
    type: ActionType.SetSeriesVisibility,
  };
}

// Update dimensions
interface LayoutDimension {
  id: string;
  relativeSize: number;
}
interface ResizeLayoutPayload {
  layout1: LayoutDimension;
  layout2: LayoutDimension;
}

interface ResizeLayoutsAction extends Action<ResizeLayoutPayload> {}
export function isResizeLayoutsAction(
  action: Action<unknown>,
): action is ResizeLayoutsAction {
  return action.type === ActionType.ResizeLayouts;
}
export function resizeLayouts(
  layout1: LayoutDimension,
  layout2: LayoutDimension,
): ResizeLayoutsAction {
  return {
    payload: {
      layout1,
      layout2,
    },
    type: ActionType.ResizeLayouts,
  };
}

// Set all layouts resizing
interface SetAllLayoutsResizingPayload {
  isResizing: boolean;
}
interface SetAllLayoutsResizingAction extends Action<SetAllLayoutsResizingPayload> {}
export function isSetAllLayoutsResizingAction(
  action: Action<unknown>,
): action is SetAllLayoutsResizingAction {
  return action.type === ActionType.SetAllLayoutsResizing;
}
export function setAllLayoutsResizing(
  isResizing: boolean,
): SetAllLayoutsResizingAction {
  return {
    payload: {
      isResizing,
    },
    type: ActionType.SetAllLayoutsResizing,
  };
}

interface SetEventVisibilityPayload {
  event: EventVisibilitySubject;
  isVisible: boolean;
}
interface SetEventVisibilityAction extends Action<SetEventVisibilityPayload> {}
export function isSetEventVisibilityAction(
  action: Action<unknown>,
): action is SetEventVisibilityAction {
  return action.type === ActionType.SetEventVisibility;
}
/**
 * Request one event's visibility. The event's name rides along so the reducer
 * can compare the request against the workspace rule: agreement clears the
 * per-event override (the rule governs again), disagreement writes one.
 */
export function setEventVisibility(
  event: EventVisibilitySubject,
  isVisible: boolean,
): SetEventVisibilityAction {
  return {
    payload: {
      event,
      isVisible,
    },
    type: ActionType.SetEventVisibility,
  };
}

interface SetEventsVisibilityPayload {
  isVisible: boolean;
  events: EventVisibilitySubject[];
}
interface SetEventsVisibilityAction extends Action<SetEventsVisibilityPayload> {}
export function isSetVisibilityForAllEventsAction(
  action: Action<unknown>,
): action is SetEventsVisibilityAction {
  return action.type === ActionType.SetEventsVisibility;
}
/**
 * Request one visibility for many events at once (the sidebar's master
 * toggle). Diff-aware in the reducer, like {@link setEventVisibility}: only
 * events whose requested visibility disagrees with the rule get an override,
 * and agreeing events have theirs cleared — so a bulk click can never
 * permanently detach a whole workspace from its rule.
 */
export function setEventsVisibility(
  isVisible: boolean,
  events: EventVisibilitySubject[],
): SetEventsVisibilityAction {
  return {
    payload: {
      isVisible,
      events,
    },
    type: ActionType.SetEventsVisibility,
  };
}

interface ClearEventOverridesAction extends Action<undefined> {}
export function isClearEventOverridesAction(
  action: Action<unknown>,
): action is ClearEventOverridesAction {
  return action.type === ActionType.ClearEventOverrides;
}
/** Drop every per-event override, returning all events to the rule. */
export function clearEventOverrides(): ClearEventOverridesAction {
  return {
    payload: undefined,
    type: ActionType.ClearEventOverrides,
  };
}

interface SetWorkspaceSettingsPayload {
  settings: Partial<ResolvedWorkspaceSettings>;
}
interface SetWorkspaceSettingsAction extends Action<SetWorkspaceSettingsPayload> {}
export function isSetWorkspaceSettingsAction(
  action: Action<unknown>,
): action is SetWorkspaceSettingsAction {
  return action.type === ActionType.SetWorkspaceSettings;
}
/**
 * Merges `settings` into `State.settings`. Fields absent from `settings` keep
 * their current value, and there is deliberately no way to clear one: session
 * settings are dense from seed onward (see `settings.ts`), and the reducer
 * drops `undefined` entries, so a partial update can never punch a hole in
 * density. The reducer filter is the sole enforcement — the payload type
 * documents intent but cannot reject `undefined`
 * (`exactOptionalPropertyTypes` is off).
 *
 * The merge is shallow, so an `events` rule replaces the previous rule whole
 * rather than merging into it.
 */
export function setWorkspaceSettings(
  settings: Partial<ResolvedWorkspaceSettings>,
): SetWorkspaceSettingsAction {
  return {
    payload: { settings },
    type: ActionType.SetWorkspaceSettings,
  };
}

interface SetImagePanelConfigPayload {
  panelId: string;
  config: Partial<ImagePanelConfig>;
}
interface SetImagePanelConfigAction extends Action<SetImagePanelConfigPayload> {}
export function isSetImagePanelConfigAction(
  action: Action<unknown>,
): action is SetImagePanelConfigAction {
  return action.type === ActionType.SetImagePanelConfig;
}
export function setImagePanelConfig(
  panelId: string,
  config: Partial<ImagePanelConfig>,
): SetImagePanelConfigAction {
  return {
    payload: {
      panelId,
      config,
    },
    type: ActionType.SetImagePanelConfig,
  };
}

interface SetLogPanelConfigPayload {
  panelId: string;
  config: Partial<LogPanelConfig>;
}
interface SetLogPanelConfigAction extends Action<SetLogPanelConfigPayload> {}
export function isSetLogPanelConfigAction(
  action: Action<unknown>,
): action is SetLogPanelConfigAction {
  return action.type === ActionType.SetLogPanelConfig;
}
export function setLogPanelConfig(
  panelId: string,
  config: Partial<LogPanelConfig>,
): SetLogPanelConfigAction {
  return {
    payload: {
      panelId,
      config,
    },
    type: ActionType.SetLogPanelConfig,
  };
}

interface SetLayoutResizingPayload {
  layoutIds: string[];
  isResizing: boolean;
}
interface SetLayoutResizingAction extends Action<SetLayoutResizingPayload> {}
export function isSetLayoutResizingAction(
  action: Action<unknown>,
): action is SetLayoutResizingAction {
  return action.type === ActionType.SetLayoutResizing;
}
export function setLayoutResizing(
  layoutIds: string[],
  isResizing: boolean,
): SetLayoutResizingAction {
  return {
    payload: {
      layoutIds,
      isResizing,
    },
    type: ActionType.SetLayoutResizing,
  };
}

// SetPanelTitle
interface SetPanelTitlePayload {
  panelId: string;
  title: string;
}
interface SetPanelTitleAction extends Action<SetPanelTitlePayload> {}
export function isSetPanelTitleAction(
  action: Action<unknown>,
): action is SetPanelTitleAction {
  return action.type === ActionType.SetPanelTitle;
}
export function setPanelTitle(
  panelId: string,
  title: string,
): SetPanelTitleAction {
  return {
    payload: {
      panelId,
      title,
    },
    type: ActionType.SetPanelTitle,
  };
}

interface SetDataSourcePayload {
  topicData: TopicData | TopicData[];
  panelId: string;
  /** seriesId for a plot panel, clip id for an image panel, path id for a map panel, etc */
  segmentId?: string;
}
interface SetDataSourceAction extends Action<SetDataSourcePayload> {}
export function isSetDataSourceAction(
  action: Action<unknown>,
): action is SetDataSourceAction {
  return action.type === ActionType.SetDataSource;
}
export function setDataSource(
  payload: SetDataSourcePayload,
): SetDataSourceAction {
  return {
    payload,
    type: ActionType.SetDataSource,
  };
}

interface SetPlotPanelConfigPayload {
  panelId: string;
  config: Partial<PlotPanelConfig>;
}
interface SetPlotPanelConfigAction extends Action<SetPlotPanelConfigPayload> {}
export function isSetPlotPanelConfigAction(
  action: Action<unknown>,
): action is SetPlotPanelConfigAction {
  return action.type === ActionType.SetPlotPanelConfig;
}
export function setPlotPanelConfig(
  panelId: string,
  config: Partial<PlotPanelConfig>,
): SetPlotPanelConfigAction {
  return {
    payload: {
      panelId,
      config,
    },
    type: ActionType.SetPlotPanelConfig,
  };
}

// SetThreeDPanelConfig
interface SetThreeDPanelConfigPayload {
  panelId: string;
  config: Partial<ThreeDPanelConfig>;
}
interface SetThreeDPanelConfigAction extends Action<SetThreeDPanelConfigPayload> {}
export function isSetThreeDPanelConfigAction(
  action: Action<unknown>,
): action is SetThreeDPanelConfigAction {
  return action.type === ActionType.SetThreeDPanelConfig;
}
export function setThreeDPanelConfig(
  panelId: string,
  config: Partial<ThreeDPanelConfig>,
): SetThreeDPanelConfigAction {
  return {
    payload: {
      panelId,
      config,
    },
    type: ActionType.SetThreeDPanelConfig,
  };
}

// AddThreeDEntity
interface AddThreeDEntityPayload {
  panelId: string;
  entity: ThreeDEntity;
}
interface AddThreeDEntityAction extends Action<AddThreeDEntityPayload> {}
export function isAddThreeDEntityAction(
  action: Action<unknown>,
): action is AddThreeDEntityAction {
  return action.type === ActionType.AddThreeDEntity;
}
export function addThreeDEntity(
  panelId: string,
  entity: ThreeDEntity,
): AddThreeDEntityAction {
  return {
    payload: {
      panelId,
      entity,
    },
    type: ActionType.AddThreeDEntity,
  };
}

// SetThreeDModel
interface SetThreeDModelPayload {
  panelId: string;
  /** The entity that will draw the model — the one whose Mesh names the file. */
  entityId: string;
  /** The model file itself: the glTF or GLB whose nodes are drawn. */
  file: ThreeDFile;
  /**
   * The robot description `file` was generated from, when there was one.
   *
   * A URDF ingested into the platform produces a glTF artifact. The user picked
   * the URDF, so the URDF's name is what the settings must show — the artifact's
   * path is an implementation detail they never chose. Carrying it also makes
   * staleness checkable later.
   */
  source?: ThreeDFile;
  /**
   * The model's joints, recorded on the Mesh beside the file.
   *
   * A pick leaves this out: the panel reads the joints from the file when it
   * loads it, and records them with {@link setThreeDModelJoints}.
   */
  joints?: ThreeDJoint[];
  /**
   * What each joint is driven by, keyed by joint name.
   *
   * Replaces the entity's previous `jointValues` wholesale, exactly as `joints`
   * does — a different file's joint values would otherwise survive onto a robot
   * that has no such joints. Carrying "the user's existing choices, kept" and
   * "the file's declared defaults, applied where they made none" through one
   * field is deliberate: the merge rule is `withDefaultBindings`, stated once,
   * rather than a second rule hidden in the reducer.
   */
  jointValues?: Record<string, ThreeDJointValue>;
}
interface SetThreeDModelAction extends Action<SetThreeDModelPayload> {}
export function isSetThreeDModelAction(
  action: Action<unknown>,
): action is SetThreeDModelAction {
  return action.type === ActionType.SetThreeDModel;
}
/**
 * Points one entity at a model file.
 *
 * One write rather than a batch: a model is drawn by a single `model` mesh
 * component that owns the file's node hierarchy, so there are no per-node
 * entities to add and no hierarchy to keep consistent across intermediate
 * states. Picking a model commits only the file, its source, and the joint
 * values that survive the pick; the panel fetches the file itself and records
 * its joints with {@link setThreeDModelJoints} once it has read them.
 *
 * Distinct from {@link updateThreeDEntity} with a mesh, because this replaces
 * the joint list wholesale: joints belong to the file, and a different file's
 * joints must not be merged with the previous one's.
 */
export function setThreeDModel(
  panelId: string,
  payload: Omit<SetThreeDModelPayload, "panelId">,
): SetThreeDModelAction {
  return {
    payload: { panelId, ...payload },
    type: ActionType.SetThreeDModel,
  };
}

// SetThreeDModelJoints
interface SetThreeDModelJointsPayload {
  panelId: string;
  /** The entity whose model the joints were read from. */
  entityId: string;
  /**
   * The file the joints were read from. They are recorded only while the
   * entity still draws this file, so a load that finishes after the user has
   * picked another model cannot put the old file's joints on the new one.
   */
  file: ThreeDFile;
  /** Every joint the file declares, as `readJointsFromArticulation` reads them. */
  joints: ThreeDJoint[];
  /**
   * The joint values to store, replacing the model's previous ones wholesale.
   *
   * Computed by the panel with `withDefaultBindings`: the values already
   * decided, plus the file's declared defaults for the joints nobody has
   * decided about. Stating that rule once, there, is why this carries the
   * result rather than the defaults.
   */
  jointValues?: Record<string, ThreeDJointValue>;
}
interface SetThreeDModelJointsAction extends Action<SetThreeDModelJointsPayload> {}
export function isSetThreeDModelJointsAction(
  action: Action<unknown>,
): action is SetThreeDModelJointsAction {
  return action.type === ActionType.SetThreeDModelJoints;
}
/**
 * Records the joints a model file declares, once the panel has read them.
 *
 * The second half of picking a model: {@link setThreeDModel} commits the file,
 * the panel fetches and parses it, and this records what the parse found so
 * the settings can list the joints without fetching the file again. Also how a
 * workspace saved before joints were recorded gets them, the first time the
 * panel loads its model.
 *
 * Touches only `joints` and `jointValues`. Everything else the mesh records,
 * such as its source or point size, is left as it is.
 */
export function setThreeDModelJoints(
  panelId: string,
  payload: Omit<SetThreeDModelJointsPayload, "panelId">,
): SetThreeDModelJointsAction {
  return {
    payload: { panelId, ...payload },
    type: ActionType.SetThreeDModelJoints,
  };
}

// UpdateThreeDEntity
interface UpdateThreeDEntityPayload {
  panelId: string;
  entityId: string;
  updates: Partial<ThreeDEntity>;
}
interface UpdateThreeDEntityAction extends Action<UpdateThreeDEntityPayload> {}
export function isUpdateThreeDEntityAction(
  action: Action<unknown>,
): action is UpdateThreeDEntityAction {
  return action.type === ActionType.UpdateThreeDEntity;
}
export function updateThreeDEntity(
  panelId: string,
  entityId: string,
  updates: Partial<ThreeDEntity>,
): UpdateThreeDEntityAction {
  return {
    payload: {
      panelId,
      entityId,
      updates,
    },
    type: ActionType.UpdateThreeDEntity,
  };
}

// RenameThreeDEntity
interface RenameThreeDEntityPayload {
  panelId: string;
  fromId: string;
  toId: string;
}
interface RenameThreeDEntityAction extends Action<RenameThreeDEntityPayload> {}
export function isRenameThreeDEntityAction(
  action: Action<unknown>,
): action is RenameThreeDEntityAction {
  return action.type === ActionType.RenameThreeDEntity;
}
/**
 * Renames an entity, rewriting every reference to it.
 *
 * Distinct from {@link updateThreeDEntity} because an id is not just a field:
 * it is the entity's key in `data.entities`, and it is referenced by its
 * children's `parent` and by `config.camera.target.id`. Those all have to move
 * together, so the reducer does it in one case rather than leaving a window in
 * which a child points at an id that no longer exists.
 *
 * A no-op if `toId` is empty, is only whitespace, or already names another
 * entity.
 */
export function renameThreeDEntity(
  panelId: string,
  fromId: string,
  toId: string,
): RenameThreeDEntityAction {
  return {
    payload: {
      panelId,
      fromId,
      toId,
    },
    type: ActionType.RenameThreeDEntity,
  };
}

// RemoveThreeDEntity
interface RemoveThreeDEntityPayload {
  panelId: string;
  entityId: string;
  cascade: boolean;
}
interface RemoveThreeDEntityAction extends Action<RemoveThreeDEntityPayload> {}
export function isRemoveThreeDEntityAction(
  action: Action<unknown>,
): action is RemoveThreeDEntityAction {
  return action.type === ActionType.RemoveThreeDEntity;
}
/**
 * Removes an entity, and decides what becomes of anything parented to it.
 *
 * `cascade: true` removes the entity's whole subtree. `cascade: false` removes
 * only this entity and splices its children up to its own parent, so no
 * configuration is lost. The caller asks the user which they meant when the
 * entity actually has children.
 *
 * A no-op when it would empty the panel — `threeDPanelDataSchema` requires at
 * least one entity.
 */
export function removeThreeDEntity(
  panelId: string,
  entityId: string,
  cascade = false,
): RemoveThreeDEntityAction {
  return {
    payload: {
      panelId,
      entityId,
      cascade,
    },
    type: ActionType.RemoveThreeDEntity,
  };
}

// SetDocumentPanelFile
interface SetDocumentPanelFilePayload {
  panelId: string;
  data: DocumentPanelData;
}
interface SetDocumentPanelFileAction extends Action<SetDocumentPanelFilePayload> {}
export function isSetDocumentPanelFileAction(
  action: Action<unknown>,
): action is SetDocumentPanelFileAction {
  return action.type === ActionType.SetDocumentPanelFile;
}
export function setDocumentPanelFile(
  panelId: string,
  data: DocumentPanelData,
): SetDocumentPanelFileAction {
  return {
    payload: {
      panelId,
      data,
    },
    type: ActionType.SetDocumentPanelFile,
  };
}
